import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { getOrCreateCustomer, createPayment } from '../services/asaas';

const { 
  FiX, FiCreditCard, FiCheckCircle, FiAlertCircle, 
  FiExternalLink, FiClock, FiZap, FiAward, FiShield
} = FiIcons;

// PLANOS EXATOS CONFORME SOLICITADO
const PACKAGES = [
  { id: 'p1', credits: 1, bonus: 0, price: 39.99, label: 'Essencial' },
  { id: 'p2', credits: 5, bonus: 1, price: 179.99, label: 'Profissional' },
  { id: 'p3', credits: 10, bonus: 3, price: 329.99, label: 'Especialista', popular: true },
  { id: 'p4', credits: 20, bonus: 8, price: 599.99, label: 'Empresarial' },
  { id: 'p5', credits: 50, bonus: 25, price: 1399.99, label: 'Corporativo' }
];

export default function BuyCreditsModal({ isOpen, onClose, profile, onOpenSettings }) {
  const [step, setStep] = useState('package');
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [method, setMethod] = useState('PIX');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  if (!isOpen) return null;

  const handlePurchase = async () => {
    if (!profile?.cpf_cnpj && method !== 'CREDIT_CARD') {
      setError('CPF/CNPJ obrigatório para PIX/Boleto');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await getOrCreateCustomer();
      const totalCredits = selectedPackage.credits + selectedPackage.bonus;
      const res = await createPayment(selectedPackage.price, totalCredits, method);
      
      if (res.success && res.invoiceUrl) {
        // Abre em nova aba
        window.open(res.invoiceUrl, '_blank');
        setStep('waiting');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-4">
      <motion.div 
        initial={{ opacity: 0 }} animate={{ opacity: 1 }}
        className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={onClose} 
      />
      
      <motion.div 
        initial={{ y: "100%" }} animate={{ y: 0 }}
        className="relative w-full max-w-xl bg-white rounded-t-[2rem] sm:rounded-[2rem] shadow-2xl overflow-hidden max-h-[90vh] flex flex-col"
      >
        <div className="p-6 overflow-y-auto">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-bold text-slate-900">Adicionar Créditos</h2>
            <button onClick={onClose} className="p-2 bg-slate-100 rounded-full"><SafeIcon icon={FiX} /></button>
          </div>

          {step === 'package' ? (
            <div className="space-y-4">
              <div className="grid grid-cols-1 gap-3">
                {PACKAGES.map(pkg => (
                  <button
                    key={pkg.id}
                    onClick={() => setSelectedPackage(pkg)}
                    className={`flex items-center justify-between p-4 rounded-2xl border-2 transition-all ${
                      selectedPackage?.id === pkg.id ? 'border-indigo-600 bg-indigo-50' : 'border-slate-100 bg-white'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-xl ${selectedPackage?.id === pkg.id ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-400'}`}>
                        <SafeIcon icon={FiZap} />
                      </div>
                      <div className="text-left">
                        <p className="font-bold text-slate-900">{pkg.label}</p>
                        <p className="text-xs text-slate-500">{pkg.credits} + {pkg.bonus} bônus</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-indigo-600 font-mono">R$ {pkg.price}</p>
                    </div>
                  </button>
                ))}
              </div>

              {selectedPackage && (
                <div className="pt-4 space-y-4">
                  <div className="flex gap-2 p-1 bg-slate-100 rounded-xl">
                    {['PIX', 'CREDIT_CARD', 'BOLETO'].map(m => (
                      <button
                        key={m}
                        onClick={() => setMethod(m)}
                        className={`flex-1 py-2 text-xs font-bold rounded-lg transition-all ${
                          method === m ? 'bg-white shadow text-indigo-600' : 'text-slate-500'
                        }`}
                      >
                        {m === 'CREDIT_CARD' ? 'Cartão' : m}
                      </button>
                    ))}
                  </div>

                  {error && <p className="text-xs text-red-500 font-medium">{error}</p>}

                  <button
                    onClick={handlePurchase}
                    disabled={loading}
                    className="w-full py-4 bg-indigo-600 text-white rounded-2xl font-bold shadow-lg shadow-indigo-200 flex items-center justify-center gap-2"
                  >
                    {loading ? 'Processando...' : `Pagar R$ ${selectedPackage.price}`}
                    <SafeIcon icon={FiExternalLink} />
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8 space-y-6">
              <div className="w-20 h-20 bg-indigo-100 text-indigo-600 rounded-full flex items-center justify-center mx-auto animate-pulse">
                <SafeIcon icon={FiClock} className="w-10 h-10" />
              </div>
              <div className="space-y-2">
                <h3 className="text-xl font-bold text-slate-900">Aguardando Pagamento</h3>
                <p className="text-sm text-slate-500 px-4">
                  O checkout foi aberto em uma nova aba. Assim que concluir o pagamento, seus créditos cairão automaticamente.
                </p>
              </div>
              <button 
                onClick={() => window.location.reload()}
                className="w-full py-4 bg-emerald-500 text-white rounded-2xl font-bold"
              >
                Já paguei, atualizar saldo
              </button>
            </div>
          )}
        </div>
        
        <div className="bg-slate-50 p-4 border-t border-slate-100 flex items-center justify-center gap-2 text-[10px] text-slate-400 font-medium uppercase tracking-wider">
          <SafeIcon icon={FiShield} className="w-3 h-3" />
          Pagamento Processado via Asaas Gateway Seguro
        </div>
      </motion.div>
    </div>
  );
}